/**
 * NavigationButtonBase - Shared base class for wf-back-btn and wf-next-btn
 *
 * Provides common functionality:
 * - Nav state event handling
 * - Wizard form discovery
 * - State request lifecycle
 */

import { LitElement } from 'lit';
import { state } from 'lit/decorators.js';

export interface NavStateDetail {
  currentStep: number;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  isSubmitting: boolean;
}

export abstract class NavigationButtonBase extends LitElement {
  // ============================================
  // Shared State
  // ============================================

  @state()
  protected _isFirstStep = true;

  @state()
  protected _isLastStep = false;

  @state()
  protected _isSubmitting = false;

  // ============================================
  // Lifecycle
  // ============================================

  override connectedCallback(): void {
    super.connectedCallback();
    this._findWizardForm()?.addEventListener('wf:nav-state', this._handleNavState as EventListener);
    this._requestNavState();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._findWizardForm()?.removeEventListener('wf:nav-state', this._handleNavState as EventListener);
  }

  // ============================================
  // Event Handlers
  // ============================================

  protected _handleNavState = (e: CustomEvent<NavStateDetail>): void => {
    this._isFirstStep = e.detail.isFirstStep;
    this._isLastStep = e.detail.isLastStep;
    this._isSubmitting = e.detail.isSubmitting;
  };

  // ============================================
  // Helpers
  // ============================================

  protected _findWizardForm(): HTMLElement | null {
    return this.closest('wizard-form');
  }

  protected _requestNavState(): void {
    this.dispatchEvent(
      new CustomEvent('wf:nav-state-request', {
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Get the event name to dispatch on click
   */
  protected abstract _getEventName(): string;

  /**
   * Dispatch the navigation event
   */
  protected _dispatchNavEvent(): void {
    this.dispatchEvent(
      new CustomEvent(this._getEventName(), {
        bubbles: true,
        composed: true,
      })
    );
  }
}
